import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, X, Loader2, CheckSquare, Trash2, Pencil, Save,
  List as ListIcon, CalendarDays,
} from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, useDraggable, DragOverlay,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { api } from '@/utils/api';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { formatDate } from '@/utils/formatters';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  projectId?: string;
  project?: { id: string; name: string } | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  assignedToId?: string;
}

interface Project { id: string; name: string; }
interface UserOption { id: string; firstName: string; lastName: string; }

const COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'todo', label: 'To Do', color: 'border-gray-300' },
  { key: 'in_progress', label: 'In Progress', color: 'border-blue-400' },
  { key: 'review', label: 'Review', color: 'border-yellow-400' },
  { key: 'done', label: 'Done', color: 'border-green-400' },
];

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
};

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const STATUS_OPTIONS = ['todo', 'in_progress', 'review', 'done'];

/* ── @dnd-kit helper components ── */

function TaskDroppableColumn({ columnKey, color, children }: { columnKey: string; color: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });
  return (
    <div
      ref={setNodeRef}
      className={`flex w-72 flex-shrink-0 flex-col rounded-xl border-t-2 ${color} border border-gray-200 transition-colors ${isOver ? 'bg-[var(--saffron-50)] border-[var(--ink-3)]' : 'bg-gray-50'}`}
    >
      {children}
    </div>
  );
}

function DraggableTaskCard({
  task,
  isActive,
  onEdit,
  onDelete,
  getInitials,
}: {
  task: Task;
  isActive: boolean;
  onEdit: () => void;
  onDelete: () => void;
  getInitials: (user?: { firstName: string; lastName: string } | null) => string | null;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing touch-none ${isActive || isDragging ? 'opacity-50 scale-95' : ''}`}
    >
      <div className="mb-2 flex items-start justify-between">
        <h4 className="text-sm font-medium text-gray-900 leading-tight">{task.title}</h4>
        <div className="flex items-center gap-0.5">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="ml-1 rounded p-0.5 text-gray-300 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={12} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="ml-1 rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={12} /></button>
        </div>
      </div>
      {task.project && (
        <p className="mb-1 text-xs text-[var(--ink-0)]">{task.project.name}</p>
      )}
      <div className="flex items-center justify-between">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
        </span>
        <div className="flex items-center gap-2">
          {task.dueDate && (
            <span className="text-xs text-gray-500">{formatDate(task.dueDate)}</span>
          )}
          {task.assignedTo && (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--saffron-50)] text-xs font-medium text-[var(--ink-0)]">
              {getInitials(task.assignedTo)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function TasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);

  // Add Task Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    dueDate: '',
    projectId: '',
    assignedToId: '',
  });

  // Edit Task Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '', title: '', description: '', status: 'todo', priority: 'medium',
    dueDate: '', projectId: '', assignedToId: '',
  });

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/tasks');
      setTasks(res.data.data?.tasks ?? res.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    api.get('/projects').then(res => setProjects(res.data.data?.projects ?? res.data.data ?? [])).catch(() => {});
    api.get('/auth/users').then(res => setUsers(res.data.data ?? [])).catch(() => {});
  }, [fetchTasks]);

  const tasksByStatus = (status: string) => tasks.filter(t => t.status === status);

  // @dnd-kit drag-and-drop
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDndDragStart = (event: DragStartEvent) => {
    setActiveTaskId(event.active.id as string);
  };

  const handleDndDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTaskId(null);
    if (!over) return;

    const taskId = active.id as string;
    const targetStatus = over.id as string;

    const task = tasks.find(t => t.id === taskId);
    if (!task || task.status === targetStatus) return;

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: targetStatus } : t));

    try {
      await api.put(`/tasks/${taskId}`, { status: targetStatus });
    } catch (err: any) {
      // Revert on failure
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: task.status } : t));
      alert(err.response?.data?.error || 'Failed to update task status');
    }
  };

  const activeTask = activeTaskId ? tasks.find(t => t.id === activeTaskId) : null;

  const getInitials = (user?: { firstName: string; lastName: string } | null) => {
    if (!user) return null;
    return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/tasks', {
        title: form.title,
        description: form.description || undefined,
        status: form.status,
        priority: form.priority,
        dueDate: form.dueDate || undefined,
        projectId: form.projectId || undefined,
        assignedToId: form.assignedToId || undefined,
      });
      setShowModal(false);
      setForm({ title: '', description: '', status: 'todo', priority: 'medium', dueDate: '', projectId: '', assignedToId: '' });
      fetchTasks();
    } catch (err: any) {
      const errData = err.response?.data;
      const errMsg = errData?.error || errData?.message || 'Failed to create task';
      // Show field-level validation errors if available
      if (errData?.errors && Array.isArray(errData.errors)) {
        alert(errData.errors.map((e: any) => `${e.path?.join('.') || 'field'}: ${e.message}`).join('\n'));
      } else {
        alert(errMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      fetchTasks();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete task');
    }
  };

  const openEditModal = (task: Task) => {
    setEditForm({
      id: task.id,
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'todo',
      priority: task.priority || 'medium',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      projectId: task.projectId || task.project?.id || '',
      assignedToId: task.assignedToId || task.assignedTo?.id || '',
    });
    setShowEditModal(true);
  };

  const handleEditTask = async () => {
    try {
      setEditSaving(true);
      await api.put(`/tasks/${editForm.id}`, {
        title: editForm.title,
        description: editForm.description || undefined,
        status: editForm.status,
        priority: editForm.priority,
        dueDate: editForm.dueDate || undefined,
        projectId: editForm.projectId || undefined,
        assignedToId: editForm.assignedToId || undefined,
      });
      setShowEditModal(false);
      fetchTasks();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update task');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-sm text-gray-500">Manage tasks across your projects</p>
        </div>
        <div className="flex items-center gap-3">
          {/* List ⇄ Calendar toggle */}
          <div className="inline-flex rounded-lg bg-[var(--ivory-2)] p-0.5">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-[var(--ink-0)] shadow-sm">
              <ListIcon size={14} /> List
            </span>
            <button
              onClick={() => navigate('/calendar')}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--ink-0)]"
            >
              <CalendarDays size={14} /> Calendar
            </button>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Add Task
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading tasks...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchTasks} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <CheckSquare size={40} className="mb-2" />
          <p className="text-sm">No tasks found</p>
          <p className="text-xs">Create your first task to get started</p>
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map(col => {
              const colTasks = tasksByStatus(col.key);
              return (
                <TaskDroppableColumn key={col.key} columnKey={col.key} color={col.color}>
                  {/* Column Header */}
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-t-xl border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{col.label}</h3>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {colTasks.length}
                      </span>
                    </div>
                  </div>

                  {/* Task Cards */}
                  <div className="flex-1 space-y-2 p-3 overflow-y-auto max-h-[calc(100vh-280px)]">
                    {colTasks.length === 0 ? (
                      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-8 text-gray-400">
                        <p className="text-xs">No tasks</p>
                      </div>
                    ) : (
                      colTasks.map(task => (
                        <DraggableTaskCard
                          key={task.id}
                          task={task}
                          isActive={activeTaskId === task.id}
                          onEdit={() => openEditModal(task)}
                          onDelete={() => handleDelete(task.id)}
                          getInitials={getInitials}
                        />
                      ))
                    )}
                  </div>
                </TaskDroppableColumn>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg w-72 opacity-90 rotate-2">
                <h4 className="mb-2 text-sm font-medium text-gray-900 leading-tight">{activeTask.title}</h4>
                {activeTask.project && (
                  <p className="mb-1 text-xs text-[var(--ink-0)]">{activeTask.project.name}</p>
                )}
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[activeTask.priority] || PRIORITY_COLORS.medium}`}>
                  {activeTask.priority.charAt(0).toUpperCase() + activeTask.priority.slice(1)}
                </span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add Task Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} className="!max-w-lg !bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Task</h2>
              <RequestCloseButton  className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>
                        {COLUMNS.find(c => c.key === s)?.label || s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    {PRIORITIES.map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label>
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Project</label>
                  <select
                    value={form.projectId}
                    onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">No Project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Assign To</label>
                  <select
                    value={form.assignedToId}
                    onChange={(e) => setForm({ ...form, assignedToId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton
                
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleCreate}
                disabled={saving || !form.title}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Task
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {showEditModal && (
        <Modal open onClose={() => setShowEditModal(false)} className="!max-w-lg !bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Task</h2>
              <RequestCloseButton  className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{COLUMNS.find(c => c.key === s)?.label || s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                  <select value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label>
                <input type="date" value={editForm.dueDate} onChange={e => setEditForm({ ...editForm, dueDate: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Project</label>
                  <select value={editForm.projectId} onChange={e => setEditForm({ ...editForm, projectId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">No Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Assign To</label>
                  <select value={editForm.assignedToId} onChange={e => setEditForm({ ...editForm, assignedToId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEditTask} disabled={editSaving || !editForm.title}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
